import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { Evaluation360FormNavigatorParams } from '@/navigation/Evaluation360/Evaluation360FormNavigator';
import type { ReviewTemplate } from '@/types/review/ReviewTemplate';

import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import { useGetUsersByIds } from '@/api/directory';
import {
    useImportTemplateInQuestionnaire,
    useUpdateReviewQuestionnaire,
} from '@/api/review-services/review-questionnaire';
import {
    useAmelioReviewTemplates,
    useReviewTemplates,
} from '@/api/review-services/review-template';
import { selectTemplateOnNextAtom } from '@/atoms/evaluation360';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';
import { ReviewTemplateAccessType } from '@/constants/review/ReviewTemplateAccessType';
import { ReviewTemplateStatus } from '@/constants/review/ReviewTemplateStatus';
import { useAmelioTranslations, useNavigate, useRegisterPageAction } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useAssignTemplate } from '@/hooks/template/useAssignTemplate';
import { useSelectTemplateNavigation } from '@/hooks/template/useSelectTemplateNavigation';
import { useError } from '@/hooks/utils/useError';
import { getReviewFormRouteName } from '@/navigation/utils';
import { SelectTemplate } from '@/pages/shared/SelectTemplate/SelectTemplate';
import { showWarningToast } from '@/plugins/Toast';
import { getAllCycleInstanceAssignedUserIds, getStepPath } from '@/utils/cycleInstance';
import { getReviewees } from '@/utils/reviewAudience';

const INITIAL_TEMPLATES_PAGE_SIZE = 5;
const SUBSEQUENT_TEMPLATES_PAGE_SIZE = 6;

export const SelectTemplatePage = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const navigate = useNavigate();
    const { cycleInstanceId } = useParams<Evaluation360FormNavigatorParams>();

    const [selectedTemplate, setSelectedTemplate] = useState<ReviewTemplate>();

    const {
        data: cycleInstance,
        isLoading: isFetchingCycleInstance,
        isError: isFetchingCycleInstanceError,
    } = useGetCycleInstance(cycleInstanceId, {
        includeInactiveQuestions: true,
    });

    const {
        data: paginatedTemplates,
        fetchNextPage: fetchMoreTemplates,
        hasNextPage: hasMoreTemplates,
        isLoading: isFetchingTemplates,
        isFetchingNextPage: isFetchingNextPage,
        isError: isFetchingTemplatesError,
    } = useReviewTemplates({
        pageSize: SUBSEQUENT_TEMPLATES_PAGE_SIZE,
        initialPageSize: INITIAL_TEMPLATES_PAGE_SIZE,
        subsequentPageSize: SUBSEQUENT_TEMPLATES_PAGE_SIZE,
        meta: {
            reviewType: cycleInstance?.reviewType,
        },
        options: {
            enabled: !!cycleInstance,
        },
    });

    const { data: amelioTemplates } = useAmelioReviewTemplates(cycleInstance?.reviewType, {
        enabled: !!cycleInstance,
    });

    const { mutateAsync: importTemplateInQuestionnaire } = useImportTemplateInQuestionnaire();
    const { mutateAsync: updateReviewQuestionnaire } = useUpdateReviewQuestionnaire();

    const userIds = useMemo(
        () =>
            cycleInstance ? Array.from(getAllCycleInstanceAssignedUserIds([cycleInstance])) : [],
        [cycleInstance],
    );
    const { data: users } = useGetUsersByIds({ userIds, identifier: 'select-template' });
    const previewEvaluee = useMemo(
        () => getReviewees({ reviewAudience: cycleInstance?.reviewAudience, users })[0],
        [cycleInstance?.reviewAudience, users],
    );

    const templates = useMemo(
        () => [
            ...(paginatedTemplates?.pages.flatMap((page) => page.items) ?? []),
            ...(amelioTemplates ?? []),
        ],
        [paginatedTemplates, amelioTemplates],
    );

    const displayedTemplates: ReviewTemplate[] = [
        {
            id: '',
            title: { en: t('evaluation360SelectTemplate.startFromScratch.title') },
            description: t('evaluation360SelectTemplate.startFromScratch.description'),
            reviewType: EvaluationType.EVALUATION_360,
            status: ReviewTemplateStatus.DRAFT,
            accessType: ReviewTemplateAccessType.APP_WIDE,
            reviewQuestionnaires: [],
            cycleQuestionnaires: [],
            questions: [],
        },

        ...templates,
    ];

    const onSelectTemplate = (template: ReviewTemplate) => {
        if (selectedTemplate?.id === template?.id) {
            setSelectedTemplate(undefined);
        } else {
            setSelectedTemplate(template);
        }
    };

    const handleClearTemplate = async () => {
        if (!cycleInstance?.reviewQuestionnaire?.id) {
            handleError({});
            return;
        }

        try {
            setSelectedTemplate(undefined);
            await updateReviewQuestionnaire({
                ...cycleInstance.reviewQuestionnaire,
                title: { en: t('evaluation360SelectTemplate.startFromScratch.questionTitle') },
                reviewTemplateId: null,
                reviewQuestions: [],
                reviewTemplate: undefined,
            });
        } catch (error) {
            handleError({ error });
        }
    };

    const onUseTemplate = useCallback(
        async (template: ReviewTemplate) => {
            if (!template?.id) {
                showWarningToast({
                    message: t('evaluation360SelectTemplate.validation.templateValidation'),
                });
                return;
            }

            if (!cycleInstance?.reviewQuestionnaire?.id) {
                handleError({});
                return;
            }

            try {
                setSelectedTemplate(template);

                await importTemplateInQuestionnaire({
                    questionnaireId: cycleInstance.reviewQuestionnaire.id,
                    templateId: template.id,
                });

                const routeName = getReviewFormRouteName(cycleInstance?.reviewType);

                navigate({
                    path: `${routeName}/${getStepPath(CycleInstanceSteps.QUESTIONNAIRE_CREATION)}/${cycleInstanceId}`,
                });
            } catch (error) {
                handleError({ error });
            }
        },
        [cycleInstance, cycleInstanceId, importTemplateInQuestionnaire, handleError, navigate, t],
    );

    const questionnaireCreationPath = `${getReviewFormRouteName(cycleInstance?.reviewType)}/${getStepPath(CycleInstanceSteps.QUESTIONNAIRE_CREATION)}/${cycleInstanceId}`;

    const { onNext } = useSelectTemplateNavigation({
        selectedTemplate,
        questionnaireId: cycleInstance?.reviewQuestionnaire?.id,
        hasExistingQuestions:
            (cycleInstance?.reviewQuestionnaire?.reviewQuestions?.length ?? 0) > 0,
        navigationPath: questionnaireCreationPath,
        importTemplate: importTemplateInQuestionnaire,
    });

    useAssignTemplate({
        reviewTemplateId:
            cycleInstance?.reviewQuestionnaire?.reviewTemplate?.id ||
            cycleInstance?.reviewQuestionnaire?.reviewTemplateId ||
            undefined,
        templates,
        setSelectedTemplate,
    });

    useRegisterPageAction(selectTemplateOnNextAtom, onNext);

    useFetchErrorsHandler({
        errors: [isFetchingTemplatesError, isFetchingCycleInstanceError],
    });

    return (
        <SelectTemplate
            isSelfEvaluation={cycleInstance?.reviewType === EvaluationType.SELF_EVALUATION}
            templates={displayedTemplates}
            hasMoreTemplates={hasMoreTemplates}
            isFetchingTemplates={isFetchingTemplates || isFetchingCycleInstance}
            isFetchingNextPage={isFetchingNextPage}
            selectedTemplate={selectedTemplate}
            initialTemplateId={
                cycleInstance?.reviewQuestionnaire?.reviewTemplate?.id ||
                cycleInstance?.reviewQuestionnaire?.reviewTemplateId ||
                undefined
            }
            loadMore={fetchMoreTemplates}
            onSelectTemplate={onSelectTemplate}
            onUseTemplate={onUseTemplate}
            onStartBuilding={() => onNext(true)}
            onClearTemplate={handleClearTemplate}
            previewEvaluee={previewEvaluee}
        />
    );
};
